// Stacked stone archways, toppling standing stones and landforms (sim/destruct.ts).
//
// Intact, every archway block is an instance of one of a few rounded field-stone meshes (a few
// draws for all of them); the standing stones live in the merged rock mesh (world/props.ts) and
// the spires and stone arches in the merged landform mesh (world/landforms.ts). When the sim says
// a stone went over, its fall is the same rigid-body simulation the server runs (sim/stonefall.ts,
// cannon-es, loaded on first use), started from the same Ev.Topple, so every client sees nearly
// the server's fall with nothing else on the wire. When the server's resting poses arrive
// (Ev.StoneRest) each block settles onto exactly where the server put it, which is where its
// collision now lies. A late joiner sees the blocks already lying there.
//
// Every stone is organic: a rounded, lumpy superellipsoid broken by a few flat fracture planes,
// faceted (James, 2026-10-21: "no minecraft block please").

import * as THREE from 'three';
import type { World } from '../../../sim/world';
import { hash32 } from '../../../sim/core';
import { Pose, Stone, StoneBlock, StoneKind } from '../../../sim/destruct';
import type { StoneFall } from '../../../sim/stonefall';
import { pillarGeometry } from './props';
import { landformPiece } from './landforms';
import type { Cyl } from '../../../sim/world';

type SF = typeof import('../../../sim/stonefall');
let sf: SF | null = null;
let loading: Promise<SF> | null = null;
/** Fetch the fall simulation and its physics engine (a small chunk, only once anything can fall). */
export function loadCannon() {
  if (!loading) loading = import('../../../sim/stonefall').then((m) => (sf = m));
  return loading;
}

const STEP = 1 / 60;
/** how long a block takes to settle from where this client's fall left it onto the server's resting pose */
const BLEND_SECS = 0.5;
/** archway block shapes (instanced meshes) */
const SHAPES = 3;

export interface StonesFx { stoneThud(x: number, y: number, z: number, size: number): void; dust(pos: THREE.Vector3, amount: number): void }

/** The landform mesh a toppled spire or arch is hidden from, and what its chunks are drawn with. */
export interface LandformDraw {
  rock: THREE.Mesh;
  tops: THREE.Mesh;
  runs: Map<number, { rock: number[][]; tops: number[][] }>;
  mat: THREE.Material;
  topMat: THREE.Material;
}

/** What one block drives on screen: an archway instance slot, or its own mesh. */
interface Piece { shape: number; slot: number; mesh: THREE.Object3D | null }

interface Fall {
  stone: number;
  sim: StoneFall | null;
  acc: number;
  pieces: Piece[];
  /** seconds into the settle onto the server's rest poses, or -1 before it starts */
  blend: number;
  from: Pose[];
  vy: number[];
}

export class StonesView {
  readonly group = new THREE.Group();
  private shapes: (THREE.InstancedMesh | null)[] = [];
  /** each archway block's shape and slot */
  private slots: { shape: number; slot: number }[][] = [];
  private falls: Fall[] = [];
  private started: boolean[] = [];
  fx: StonesFx | null = null;
  private m4 = new THREE.Matrix4();
  private q = new THREE.Quaternion();
  private q2 = new THREE.Quaternion();
  private q3 = new THREE.Quaternion();
  private q4 = new THREE.Quaternion();
  private v = new THREE.Vector3();
  private ax = new THREE.Vector3();
  private ax2 = new THREE.Vector3();
  private p2 = new THREE.Vector3();
  private e = new THREE.Euler();
  private p = new THREE.Vector3();
  private s = new THREE.Vector3();
  private disposables: { dispose(): void }[] = [];

  constructor(private world: World, private quality: number, private mat: THREE.Material,
    private rocks: THREE.Mesh | null, private monoRuns: Map<number, { start: number; count: number }>,
    private land: LandformDraw | null = null) {
    this.group.name = 'stones';
    const stones = world.dest.stones;
    // field stones, plus the lintel: a long slab, thick in the middle and thinning to its ends
    // (one shape for a lintel running along x, one along z)
    const counts = new Array(SHAPES + 2).fill(0);
    stones.forEach((s, si) => {
      const row: { shape: number; slot: number }[] = [];
      if (s.kind === StoneKind.Archway) s.blocks.forEach((b, k) => {
        const sh = b.hx > 2 ? SHAPES : b.hz > 2 ? SHAPES + 1 : hash32(si, k, 0x0a7c) % SHAPES;
        row.push({ shape: sh, slot: counts[sh]++ });
      });
      this.slots.push(row);
    });
    const col = new THREE.Color();
    for (let sh = 0; sh < SHAPES + 2; sh++) {
      if (!counts[sh]) { this.shapes.push(null); continue; }
      const lintel = sh >= SHAPES;
      const geo = organicStone(0x5703 + sh * 977, quality, lintel ? { n: 3.4, lump: 0.1, cuts: 6, cut: 0.78 } : { n: 3.2, lump: 0.07, cuts: 4, cut: 0.86 });
      if (lintel) {
        const p = geo.getAttribute('position') as THREE.BufferAttribute;
        for (let i = 0; i < p.count; i++) { const u = sh === SHAPES ? p.getX(i) : p.getZ(i); p.setY(i, p.getY(i) * (1.12 - 0.42 * u * u)); }
        geo.computeVertexNormals();
      }
      geo.setAttribute('color', grimeColours(geo, 0.78));
      const mesh = new THREE.InstancedMesh(geo, mat, counts[sh]);
      mesh.name = 'archwayStones';
      mesh.castShadow = true; mesh.receiveShadow = true;
      mesh.frustumCulled = false;
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      this.shapes.push(mesh);
      this.group.add(mesh);
      this.disposables.push(geo);
    }
    stones.forEach((s, si) => {
      if (s.kind !== StoneKind.Archway) return;
      s.blocks.forEach((b, k) => {
        const { shape, slot } = this.slots[si][k];
        // drawn a touch taller than its box, so each stone beds into the one below (no floating
        // seams), and turned and sized a little its own way so the stack reads as hand-piled
        const r1 = hash32(si, k, 0x51e) / 4294967296, r2 = hash32(si, k, 0x51f) / 4294967296;
        this.q.setFromEuler(this.e.set((r1 - 0.5) * 0.24, b.yaw + (r2 - 0.5) * 0.8, (r2 - 0.5) * 0.22));
        const [sx, sy, sz] = archScale(si, k);
        this.pose(b.x, b.y, b.z, this.q, b.hx * sx, b.hy * sy, b.hz * sz);
        this.shapes[shape]!.setMatrixAt(slot, this.m4);
        // weathered sandstone, each stone its own shade
        const h = hash32(si, k, 0x5a7d) / 4294967296;
        col.setRGB(0.86 + h * 0.18, 0.8 + h * 0.14, 0.68 + h * 0.1);
        this.shapes[shape]!.setColorAt(slot, col);
      });
    });
    for (const m of this.shapes) if (m?.instanceColor) m.instanceColor.needsUpdate = true;
    for (let i = 0; i < stones.length; i++) this.started.push(false);
    // anything that can fall means the engine will be wanted: fetch it while the island loads
    if (stones.length) void loadCannon();
  }

  private pose(x: number, y: number, z: number, q: THREE.Quaternion, hx: number, hy: number, hz: number) {
    this.m4.compose(this.p.set(x, y, z), q, this.s.set(hx, hy, hz));
  }

  update(dt: number) {
    const stones = this.world.dest.stones;
    for (let i = 0; i < stones.length; i++) {
      const s = stones[i];
      if (!s.toppled || this.started[i] || !s.fall) continue;
      // a late joiner: it fell before they rode in; the blocks go straight to where they lie
      const settled = s.fall.settled;
      if (!settled && !sf) { void loadCannon(); continue; }
      this.started[i] = true;
      const f = this.begin(i, s, settled);
      if (settled) { this.drawRest(f, 1); this.finish(f); }
      else this.falls.push(f);
    }
    if (!this.falls.length) return;
    const h = Math.min(dt, 0.1);
    for (let k = this.falls.length - 1; k >= 0; k--) {
      const f = this.falls[k];
      if (this.stepFall(f, h)) { this.finish(f); this.falls.splice(k, 1); }
    }
    for (const m of this.shapes) if (m) m.instanceMatrix.needsUpdate = true;
  }

  /** Swap the standing stone for pieces that can move, and start its fall. */
  private begin(i: number, s: Stone, settled: boolean): Fall {
    const f: Fall = { stone: i, sim: settled ? null : sf!.beginFall(this.world, s, i), acc: 0, pieces: [], blend: -1, from: [], vy: s.blocks.map(() => 0) };
    if (s.kind === StoneKind.Archway) f.pieces = this.slots[i].map((p) => ({ ...p, mesh: null }));
    else if (s.kind === StoneKind.Monolith) {
      const c = s.pillar, run = this.monoRuns.get(s.src);
      let mesh: THREE.Mesh | null = null;
      if (c && run && this.rocks) {
        // lift it out of the merged rock mesh into its own
        const b = s.blocks[0];
        const g = pillarGeometry(c, this.quality);
        g.translate(-b.x, -b.y, -b.z);
        mesh = this.meshOf(g, this.mat);
        mesh.position.set(b.x, b.y, b.z);
        const idx = this.rocks.geometry.index!;
        (idx.array as Uint32Array).fill(0, run.start, run.start + run.count);
        idx.addUpdateRange(run.start, run.count);
        idx.needsUpdate = true;
      }
      f.pieces = [{ shape: -1, slot: -1, mesh }];
    } else {
      // a spire or an arch: out of the landform mesh, into its stub(s) and the chunks that fall
      const L = this.land;
      const run = L?.runs.get(s.land);
      if (L && run) { hideRuns(L.rock, run.rock); hideRuns(L.tops, run.tops); }
      const mat = L?.mat ?? this.mat, topMat = L?.topMat ?? mat;
      // each piece is cut from the same strata rock as the standing landform: a drum of the
      // column (the top one keeps its grassy crown) or a slab of the deck
      const cylOf = (b: { x: number; z: number }) => {
        let best: Cyl | null = null;
        for (const k of s.cut) if (!best || Math.hypot(k.c.x - b.x, k.c.z - b.z) < Math.hypot(best.x - b.x, best.z - b.z)) best = k.c;
        return best;
      };
      const piece = (b: StoneBlock, k: number, stub: boolean) => {
        const drum = Math.abs(b.hx - b.hz) < 1e-6, c = drum ? cylOf(b) : null;
        const y0 = b.y - b.hy, y1 = b.y + b.hy;
        const crown = !stub && !!c && c.kind === 'spire' && k === s.blocks.length - 1;
        const geo = c
          ? landformPiece(this.world, { cyl: { ...c, r: b.hx / 0.82, y0, y1, variant: c.variant * 16 + k + (stub ? 8 : 0) }, whole: { y0: c.y0, y1: s.y1 } }, this.quality, crown)
          : landformPiece(this.world, { box: { kind: 'arch', x: b.x, z: b.z, hx: b.hx, hz: b.hz, y0, y1, variant: s.land * 16 + k } }, this.quality, false);
        const grp = new THREE.Group();
        const seed = hash32(s.land, k, stub ? 0x57b : 0xf2a);
        const brk = c
          ? { cx: b.x, cy: b.y, cz: b.z, axis: 1 as const, lo: y0, hi: y1, r: b.hx / 0.82, breakLo: !stub, breakHi: !crown, seed, minR: stub ? 0 : b.hx * 1.05 }
          : b.hx > b.hz
            ? { cx: b.x, cy: b.y, cz: b.z, axis: 0 as const, lo: b.x - b.hx, hi: b.x + b.hx, r: Math.min(b.hz, b.hy), breakLo: true, breakHi: true, seed }
            : { cx: b.x, cy: b.y, cz: b.z, axis: 2 as const, lo: b.z - b.hz, hi: b.z + b.hz, r: Math.min(b.hx, b.hy), breakLo: true, breakHi: true, seed };
        for (const [g, m] of [[geo.rock, mat], [geo.tops, topMat]] as const) {
          if (!g.getAttribute('position').count) { g.dispose(); continue; }
          fracture(g, brk);
          // into the piece's own frame (it starts at its block's yaw)
          g.translate(-b.x, -b.y, -b.z);
          g.rotateY(-b.yaw);
          const mesh = new THREE.Mesh(g, m);
          mesh.castShadow = true; mesh.receiveShadow = true;
          grp.add(mesh);
          this.disposables.push(g);
        }
        grp.position.set(b.x, b.y, b.z);
        grp.quaternion.setFromAxisAngle(_up, b.yaw);
        this.group.add(grp);
        return grp;
      };
      s.stumps.forEach((b, k) => piece(b, k, true));
      this.scree(s);
      f.pieces = s.blocks.map((b, k) => ({ shape: -1, slot: -1, mesh: piece(b, k, false) }));
      if (!settled && this.fx) {
        for (const b of s.blocks) this.fx.dust(this.p.set(b.x, b.y, b.z), 1.5);
        // the snap: grit and dust bursting from each break
        for (const b of s.stumps) this.fx.stoneThud(b.x, b.y + b.hy, b.z, 2);
      }
    }
    return f;
  }

  /** Broken rock knocked off where a landform snapped: a fan of sandstone chunks round each stub. */
  private scree(s: Stone) {
    const n = Math.min(40, s.stumps.length * 16);
    if (!n) return;
    const geo = organicStone(hash32(s.land, 0x5c9), Math.min(this.quality, 1), { n: 2.6, lump: 0.09, cuts: 4, cut: 0.72 });
    geo.setAttribute('color', grimeColours(geo, 0.7));
    const m = new THREE.InstancedMesh(geo, this.mat, n);
    m.castShadow = this.quality > 1; m.receiveShadow = true;
    const col = new THREE.Color();
    let i = 0;
    for (const st of s.stumps) {
      for (let k = 0; k < 16 && i < n; k++, i++) {
        const h = (t: number) => hash32(s.land, i, t) / 4294967296;
        const a = h(1) * Math.PI * 2, d = st.hx * (1.05 + h(2) * 1.6);
        const x = st.x + Math.cos(a) * d, z = st.z + Math.sin(a) * d;
        const g = this.world.terrain(x, z);
        const r = (0.25 + h(3) ** 2 * 0.9) * Math.min(1.4, st.hx / 2);
        this.q.setFromEuler(this.e.set(h(4) * 3, h(5) * 6, h(6) * 3));
        this.pose(x, g + r * 0.35, z, this.q, r * (0.9 + h(7) * 0.5), r * 0.7, r);
        m.setMatrixAt(i, this.m4);
        col.setRGB(0.9 + h(8) * 0.15, 0.72 + h(8) * 0.1, 0.52 + h(8) * 0.08);
        m.setColorAt(i, col);
      }
    }
    m.count = i;
    this.group.add(m);
    this.disposables.push(geo, m);
  }

  private meshOf(g: THREE.BufferGeometry, mat: THREE.Material) {
    const m = new THREE.Mesh(g, mat);
    m.castShadow = true; m.receiveShadow = true;
    this.group.add(m);
    this.disposables.push(g);
    return m;
  }

  /** Advance one fall by a frame; true once it lies exactly where the server says. */
  private stepFall(f: Fall, h: number) {
    const s = this.world.dest.stones[f.stone];
    if (f.blend >= 0) { f.blend += h; this.drawRest(f, Math.min(1, f.blend / BLEND_SECS)); return f.blend >= BLEND_SECS; }
    const sim = f.sim!;
    // the server's fixed step, so the fall here tracks the server's
    f.acc += h;
    for (let n = 0; f.acc >= STEP && n < 6; n++) {
      f.acc -= STEP;
      sf!.stepFall(sim, STEP);
      sim.bodies.forEach((b, k) => {
        // a heavy block slamming down throws up dust
        const vy = b.velocity.y;
        if (this.fx && f.vy[k] < -2 && vy > f.vy[k] * 0.3) {
          const big = s.kind === StoneKind.Landform ? 1.8 : 1;
          this.fx.stoneThud(b.position.x, b.position.y - s.blocks[k].hy * 0.7, b.position.z, Math.min(2 * big, (-f.vy[k] / 4) * big));
        }
        f.vy[k] = vy;
      });
    }
    this.drawBodies(f, sim);
    // the server's rest poses are in: settle onto them
    if (s.rest.every((r) => r)) {
      f.from = sim.bodies.map((b) => sf!.poseOf(b));
      f.blend = 0;
      f.sim = null; // the engine's world goes
    }
    return false;
  }

  private drawBodies(f: Fall, sim: StoneFall) {
    const s = this.world.dest.stones[f.stone];
    sim.bodies.forEach((b, k) => {
      this.q.set(b.quaternion.x, b.quaternion.y, b.quaternion.z, b.quaternion.w);
      this.place(f, s, k, b.position.x, b.position.y, b.position.z, this.q);
    });
  }

  /** Blocks between where they were and the server's resting pose (t = 1: exactly there). */
  private drawRest(f: Fall, t: number) {
    const s = this.world.dest.stones[f.stone];
    const e = t * t * (3 - 2 * t);
    s.blocks.forEach((_, k) => {
      const r = s.rest[k];
      if (!r) return;
      const a = f.from[k] ?? r;
      this.q.set(a.qx, a.qy, a.qz, a.qw).slerp(this.q2.set(r.qx, r.qy, r.qz, r.qw), e);
      this.v.set(a.x + (r.x - a.x) * e, a.y + (r.y - a.y) * e, a.z + (r.z - a.z) * e);
      if (s.kind === StoneKind.Landform && f.pieces[k].mesh) this.slump(f.stone, k, s.blocks[k], r, e);
      this.place(f, s, k, this.v.x, this.v.y, this.v.z, this.q);
    });
  }

  /**
   * A broken piece of a landform doesn't come to rest square on one face the way its collision
   * block does: it rolls a little about its length and one end digs in. Drawn only (the block
   * stays the cover), eased in over the settle so nothing pops. Works on this.q / this.v.
   */
  private slump(si: number, k: number, b: StoneBlock, r: Pose, e: number) {
    const h = (t: number) => hash32(si, k, t) / 4294967296;
    const qr = this.q2.set(r.qx, r.qy, r.qz, r.qw);
    const A = this.ax.set(0, 1, 0).applyQuaternion(qr);
    const R = this.q3.setFromAxisAngle(A, (h(0x51) - 0.5) * 1.1);
    const H = this.ax2.crossVectors(A, _up);
    if (H.lengthSq() > 0.25) {
      // lying down: tip about the cross axis through the end that stays up, so the other end sinks
      H.normalize();
      const end = h(0x52) < 0.5 ? -1 : 1;
      const th = -(0.1 + h(0x53) * 0.12) * end;
      const pivot = this.p2.set(r.x, r.y - b.hx, r.z).addScaledVector(A, -end * b.hy);
      const T = this.q4.setFromAxisAngle(H, th);
      R.premultiply(T);
      // the pivot stays put: the centre swings about it
      this.ax.set(r.x, r.y, r.z).sub(pivot).applyQuaternion(T).add(pivot).sub(this.p2.set(r.x, r.y, r.z));
    } else {
      // stood on end: lean it over and settle it in a little
      H.set(Math.cos(h(0x54) * 6.28), 0, Math.sin(h(0x54) * 6.28));
      R.premultiply(this.q4.setFromAxisAngle(H, 0.35 + h(0x55) * 0.2));
      this.ax.set(0, -b.hy * 0.3, 0);
    }
    this.q4.identity().slerp(R, e);
    this.q.premultiply(this.q4);
    this.v.addScaledVector(this.ax, e);
  }

  private place(f: Fall, s: Stone, k: number, x: number, y: number, z: number, q: THREE.Quaternion) {
    // one that tumbled off the island's edge is gone into the sky
    const gone = y < s.y0 - 20;
    const pc = f.pieces[k];
    if (pc.mesh) { pc.mesh.position.set(x, y, z); pc.mesh.quaternion.copy(q); pc.mesh.visible = !gone; return; }
    if (pc.shape < 0) return;
    const b = s.blocks[k], v = gone ? 0 : 1, [sx, sy, sz] = archScale(f.stone, k);
    this.pose(x, y, z, q, b.hx * v * sx, b.hy * v * sy, b.hz * v * sz);
    this.shapes[pc.shape]!.setMatrixAt(pc.slot, this.m4);
  }

  private finish(f: Fall) {
    f.sim = null;
    for (const m of this.shapes) if (m) m.instanceMatrix.needsUpdate = true;
    // a resting chunk never moves again: no need to recompute its matrix every frame
    for (const p of f.pieces) if (p.mesh) { p.mesh.updateMatrix(); p.mesh.matrixAutoUpdate = false; }
  }

  dispose() {
    this.group.removeFromParent();
    for (const m of this.shapes) m?.dispose();
    for (const d of this.disposables) d.dispose();
  }
}

const _up = new THREE.Vector3(0, 1, 0);

/** An archway stone's drawn size against its box (a touch taller, so stones bed into each other). */
function archScale(si: number, k: number) {
  const r1 = hash32(si, k, 0x51e) / 4294967296, r2 = hash32(si, k, 0x51f) / 4294967296;
  return [0.88 + r1 * 0.3, 1.14, 0.88 + r2 * 0.3];
}

/** Collapse vertex runs of a merged non-indexed mesh to a point: those triangles vanish. */
function hideRuns(mesh: THREE.Mesh, runs: number[][]) {
  const pos = mesh.geometry.getAttribute('position') as THREE.BufferAttribute;
  for (const [start, count] of runs) {
    if (!count) continue;
    const x = pos.getX(start), y = pos.getY(start), z = pos.getZ(start);
    for (let i = start; i < start + count; i++) pos.setXYZ(i, x, y, z);
    pos.addUpdateRange(start * 3, count * 3);
  }
  pos.needsUpdate = true;
}

/**
 * An organic stone filling a unit box (-1..1): a rounded superellipsoid (n = 2 an egg, 3-4 a
 * pillowy field stone) made lumpy by a few seeded waves and broken by flat fracture planes, then
 * stretched to touch the box on every axis (so it sits on what it's stacked on, as its physics
 * box does). Faceted.
 */
export function organicStone(seed: number, quality: number, o: { n: number; lump: number; cuts: number; cut: number; ends?: number }) {
  const ico = new THREE.IcosahedronGeometry(1, quality <= 0 ? 2 : quality <= 1 ? 3 : 4);
  const p = ico.getAttribute('position') as THREE.BufferAttribute;
  const rnd = mulberry(seed);
  const waves: number[][] = [];
  for (let i = 0; i < 6; i++) {
    const d = randDir(rnd), f = 1.2 + rnd() * 2.6;
    waves.push([d[0] * f, d[1] * f, d[2] * f, rnd() * 6.283, (o.lump / (1 + i * 0.5)) * (0.6 + rnd() * 0.8)]);
  }
  // fracture planes: where a corner split off along a bedding or joint plane
  const cuts: number[][] = [];
  for (let i = 0; i < o.cuts; i++) { const d = randDir(rnd); cuts.push([d[0], d[1], d[2], o.cut + rnd() * 0.08]); }
  // a drum broken off a column has flat-ish broken ends
  if (o.ends) for (const sy of [-1, 1]) { const d = randDir(rnd); const n = norm3(d[0] * 0.25, sy, d[2] * 0.25); cuts.push([n[0], n[1], n[2], o.ends + rnd() * 0.06]); }
  const v = new THREE.Vector3();
  const mx = [0, 0, 0];
  for (let i = 0; i < p.count; i++) {
    v.fromBufferAttribute(p, i).normalize();
    const sn = Math.pow(Math.abs(v.x) ** o.n + Math.abs(v.y) ** o.n + Math.abs(v.z) ** o.n, 1 / o.n);
    v.multiplyScalar(1 / sn);
    let w = 1;
    for (const a of waves) w += a[4] * Math.sin(a[0] * v.x + a[1] * v.y + a[2] * v.z + a[3]);
    v.multiplyScalar(w);
    for (const c of cuts) {
      const d = v.x * c[0] + v.y * c[1] + v.z * c[2];
      if (d > c[3]) { const k = d - c[3]; v.x -= c[0] * k; v.y -= c[1] * k; v.z -= c[2] * k; }
    }
    p.setXYZ(i, v.x, v.y, v.z);
    mx[0] = Math.max(mx[0], Math.abs(v.x)); mx[1] = Math.max(mx[1], Math.abs(v.y)); mx[2] = Math.max(mx[2], Math.abs(v.z));
  }
  for (let i = 0; i < p.count; i++) p.setXYZ(i, p.getX(i) / mx[0], p.getY(i) / mx[1], p.getZ(i) / mx[2]);
  const g = ico.index ? ico.toNonIndexed() : ico;
  if (g !== ico) ico.dispose();
  g.deleteAttribute('uv');
  g.computeVertexNormals();
  return g;
}

/** Vertex colour for a field stone: grimy low down, a little brighter where it faces the sky. */
function grimeColours(g: THREE.BufferGeometry, low: number) {
  const pos = g.getAttribute('position') as THREE.BufferAttribute, nrm = g.getAttribute('normal') as THREE.BufferAttribute;
  const col = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const c = (low + (1 - low) * Math.min(1, (pos.getY(i) + 1) * 0.8)) * (0.94 + 0.08 * Math.max(0, nrm.getY(i)));
    col[i * 3] = c; col[i * 3 + 1] = c * 0.97; col[i * 3 + 2] = c * 0.93;
  }
  return new THREE.BufferAttribute(col, 3);
}

function mulberry(seed: number) {
  let a = seed >>> 0;
  return () => { a = (a + 0x6d2b79f5) >>> 0; let t = a; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}
function randDir(r: () => number) { const z = r() * 2 - 1, a = r() * Math.PI * 2, s = Math.sqrt(1 - z * z); return [Math.cos(a) * s, z, Math.sin(a) * s]; }
function norm3(x: number, y: number, z: number) { const l = Math.hypot(x, y, z) || 1; return [x / l, y / l, z / l]; }

/**
 * Break a fallen piece's cut ends. A landform piece comes out of landformPiece() with flat, sawn
 * ends and a round outline, so it reads as a log or a drum. This turns each broken end into a
 * sloped, jagged fracture of paler fresh rock and bites a few flats out of the outline, so the
 * piece reads as rock that snapped. `axis` is the piece's long axis (0 x, 1 y, 2 z), `lo`/`hi`
 * its extent along it (world units), `r` its cross-section radius; `breakLo`/`breakHi` say
 * which ends broke (a spire's top keeps its weathered crown, a stub only broke at the top).
 */
function fracture(g: THREE.BufferGeometry, o: { cx: number; cy: number; cz: number; axis: 0 | 1 | 2; lo: number; hi: number; r: number; breakLo: boolean; breakHi: boolean; seed: number; minR?: number }) {
  const pos = g.getAttribute('position') as THREE.BufferAttribute;
  const col = g.getAttribute('color') as THREE.BufferAttribute | undefined;
  const nrm = g.getAttribute('normal') as THREE.BufferAttribute;
  const P = pos.array as Float32Array, n = pos.count;
  const rnd = mulberry(o.seed);
  const R = o.r;
  const band = Math.min(0.9 * R, (o.hi - o.lo) * 0.38);
  const k = band / (0.9 * R);
  // the outline: two or three flats split off down the length, plus a slow lobe
  const flats = 2 + Math.floor(rnd() * 2);
  const fa: number[] = [], fd: number[] = [];
  for (let i = 0; i < flats; i++) { fa.push(rnd() * Math.PI * 2); fd.push(0.18 + rnd() * 0.14); }
  const lobeA = rnd() * Math.PI * 2;
  // a wedge: one side of the outline closes in toward one end, so no two ends match
  const wedgeA = rnd() * Math.PI * 2, wedge = 0.18 + rnd() * 0.17, wedgeUp = rnd() < 0.5;
  // each end's break: a tilted plane (which way it sheared), stepped ridges and conchoidal chips
  const end = () => {
    // always a clear shear: a break is never square to the column
    const sa = rnd() * Math.PI * 2, sm = 0.7 + rnd() * 0.3;
    return { su: Math.cos(sa) * sm, sv: Math.sin(sa) * sm, p1: rnd() * 6.3, p2: rnd() * 6.3, p3: rnd() * 6.3, step: 0.6 + rnd() * 0.4 };
  };
  const eLo = end(), eHi = end();
  const depth = (e: ReturnType<typeof end>, u: number, v: number) => {
    const x = u / R, y = v / R;
    let f = 0.34 + 0.28 * (e.su * x + e.sv * y);
    // a couple of stepped ridges across the break, then finer chips
    f += 0.14 * Math.abs(Math.sin(x * 2.3 + y * 1.1 + e.p1)) * e.step;
    f += 0.09 * Math.sin(x * 5.1 + e.p2) * Math.sin(y * 4.7 + e.p3);
    return Math.max(0.04, f) * R * k;
  };
  const ax = o.axis, bu = ax === 0 ? 2 : 0, bv = ax === 1 ? 2 : 1;
  const c = [o.cx, o.cy, o.cz];
  const w = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const s = P[i * 3 + ax];
    let u = P[i * 3 + bu] - c[bu], v = P[i * 3 + bv] - c[bv];
    const a = Math.atan2(v, u);
    let e = 1 + 0.09 * Math.sin(3 * a + lobeA) + 0.04 * Math.sin(5 * a + lobeA * 2);
    for (let f = 0; f < flats; f++) e -= fd[f] * Math.max(0, Math.cos(a - fa[f])) ** 4;
    const wl = o.breakLo ? Math.max(0, 1 - (s - o.lo) / band) : 0;
    const wh = o.breakHi ? Math.max(0, 1 - (o.hi - s) / band) : 0;
    const wi = Math.max(wl, wh);
    const t = Math.min(1, Math.max(0, (s - o.lo) / (o.hi - o.lo)));
    e *= 1 - wedge * (wedgeUp ? t : 1 - t) * Math.max(0, Math.cos(a - wedgeA));
    // the drawn piece is ~1/0.82 its collision radius: never bite in past that, or it hovers
    e = Math.max(0.84, e);
    // the broken lip chips back unevenly
    e *= 1 - 0.1 * wi * (0.5 + 0.5 * Math.sin(a * 7 + o.seed));
    u *= e; v *= e;
    let ns = s;
    if (wl > 0) ns += depth(eLo, u, v) * wl;
    if (wh > 0) ns -= depth(eHi, u, v) * wh;
    // a weathered (unbroken) top is worn round at the rim, not a flat lid
    if (!o.breakHi) {
      const wr = Math.max(0, 1 - (o.hi - s) / band), q = Math.min(1, Math.hypot(u, v) / R);
      ns -= 0.35 * R * k * q * q * wr;
    }
    P[i * 3 + ax] = ns; P[i * 3 + bu] = c[bu] + u; P[i * 3 + bv] = c[bv] + v;
    w[i] = wi;
  }
  // a spire narrows toward its crown, so a piece cut high up can be drawn thinner than the block
  // it rests on and would hover: fatten it until most of its outline reaches the collision
  if (o.minR) {
    const rho: number[] = [];
    for (let i = 0; i < n; i++) if (w[i] === 0) rho.push(Math.hypot(P[i * 3 + bu] - c[bu], P[i * 3 + bv] - c[bv]));
    rho.sort((x, y) => x - y);
    const p30 = rho.length ? rho[Math.floor(rho.length * 0.3)] : o.minR;
    const fat = p30 > 0 ? Math.max(1, o.minR / p30) : 1;
    if (fat > 1) for (let i = 0; i < n; i++) {
      P[i * 3 + bu] = c[bu] + (P[i * 3 + bu] - c[bu]) * fat;
      P[i * 3 + bv] = c[bv] + (P[i * 3 + bv] - c[bv]) * fat;
    }
  }
  // fresh rock is paler; the break's facets get flat normals so they catch the low sun
  const N = nrm.array as Float32Array, C = col?.array as Float32Array | undefined;
  const t1 = new THREE.Vector3(), t2 = new THREE.Vector3(), t3 = new THREE.Vector3();
  for (let t = 0; t < n; t += 3) {
    const wm = Math.max(w[t], w[t + 1], w[t + 2]);
    if (wm < 0.2) continue;
    t1.fromArray(P, t * 3); t2.fromArray(P, t * 3 + 3).sub(t1); t3.fromArray(P, t * 3 + 6).sub(t1);
    t2.cross(t3).normalize();
    for (let j = 0; j < 3; j++) {
      t2.toArray(N, (t + j) * 3);
      if (C) for (let ch = 0; ch < 3; ch++) C[(t + j) * 3 + ch] = Math.min(1, C[(t + j) * 3 + ch] * (1 + 0.22 * w[t + j]));
    }
  }
  pos.needsUpdate = true; nrm.needsUpdate = true;
  if (col) col.needsUpdate = true;
  g.computeBoundingSphere(); g.computeBoundingBox();
}
